"use client";

import { useRef, useState } from "react";
import {
	ArtistIcon,
	DSFont,
} from "@/components/design-system/ArtistDesignSystem";

export interface GenArtViewer360Props {
	readonly onClose: () => void;
	readonly tint: string;
}
interface DragState {
	readonly pointerId: number;
	readonly x: number;
	readonly y: number;
	readonly yaw: number;
	readonly pitch: number;
}

export function GenArtViewer360({
	onClose,
	tint,
}: Readonly<GenArtViewer360Props>) {
	const [yaw, setYaw] = useState(-18);
	const [pitch, setPitch] = useState(-8);
	const dragRef = useRef<DragState | null>(null);

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label="360 模型查看器"
			style={{
				position: "absolute",
				inset: 0,
				overflow: "hidden",
				background:
					"radial-gradient(120% 80% at 50% 40%, color-mix(in srgb, #1a1340 80%, transparent) 0%, #05040A 70%)",
			}}
			onKeyDown={(event) => {
				if (event.key === "Escape") onClose();
			}}
		>
			<button
				type="button"
				aria-label="返回"
				onClick={onClose}
				style={{
					position: "absolute",
					top: "max(16px, env(safe-area-inset-top, 0px))",
					left: 16,
					zIndex: 2,
					width: 44,
					height: 44,
					borderRadius: "50%",
					display: "grid",
					placeItems: "center",
					color: "var(--ds-on-media-primary)",
					background: "rgb(0 0 0 / 0.42)",
					border:
						"1px solid color-mix(in srgb, var(--ds-on-media-primary) 30%, transparent)",
					cursor: "pointer",
				}}
			>
				<ArtistIcon name="back" size={20} />
			</button>

			<div
				role="application"
				// biome-ignore lint/a11y/noNoninteractiveTabindex: this keyboard-operated 360 viewer must receive focus.
				tabIndex={0}
				aria-label="可旋转的 360 模型。拖动或使用方向键旋转，按零键复位。"
				style={{
					position: "absolute",
					inset: 0,
					display: "grid",
					placeItems: "center",
					perspective: 900,
					cursor: dragRef.current === null ? "grab" : "grabbing",
					touchAction: "none",
					outline: "none",
				}}
				onPointerDown={(event) => {
					event.currentTarget.setPointerCapture(event.pointerId);
					dragRef.current = {
						pointerId: event.pointerId,
						x: event.clientX,
						y: event.clientY,
						yaw,
						pitch,
					};
				}}
				onPointerMove={(event) => {
					const drag = dragRef.current;
					if (drag === null || drag.pointerId !== event.pointerId) return;
					setYaw(drag.yaw + (event.clientX - drag.x) * 0.45);
					setPitch(
						Math.max(-70, Math.min(70, drag.pitch - (event.clientY - drag.y) * 0.35)),
					);
				}}
				onPointerUp={(event) => {
					if (dragRef.current?.pointerId === event.pointerId) {
						dragRef.current = null;
						event.currentTarget.releasePointerCapture(event.pointerId);
					}
				}}
				onPointerCancel={() => {
					dragRef.current = null;
				}}
				onKeyDown={(event) => {
					if (event.key === "ArrowLeft") setYaw((value) => value - 8);
					else if (event.key === "ArrowRight") setYaw((value) => value + 8);
					else if (event.key === "ArrowUp") setPitch((value) => Math.min(70, value + 8));
					else if (event.key === "ArrowDown") setPitch((value) => Math.max(-70, value - 8));
					else if (event.key === "0") {
						setYaw(-18);
						setPitch(-8);
					} else return;
					event.preventDefault();
				}}
			>
				<div
					aria-hidden="true"
					style={{
						position: "relative",
						width: "min(66vw, 340px)",
						aspectRatio: "1",
						transformStyle: "preserve-3d",
						transform: `rotateX(${pitch}deg) rotateY(${yaw}deg)`,
						transition: dragRef.current === null ? "transform 120ms ease-out" : "none",
					}}
				>
					{[-72, -36, 0, 36, 72].map((rotation) => (
						<div
							key={rotation}
							style={{
								position: "absolute",
								inset: "7%",
								borderRadius: "42% 58% 54% 46% / 48% 44% 56% 52%",
								border: `2px solid color-mix(in srgb, ${tint} 70%, white)`,
								background: `linear-gradient(135deg, color-mix(in srgb, ${tint} 48%, transparent), color-mix(in srgb, ${tint} 10%, #090615))`,
								boxShadow: `inset 0 0 56px color-mix(in srgb, ${tint} 34%, transparent), 0 0 34px color-mix(in srgb, ${tint} 16%, transparent)`,
								transform: `rotateY(${rotation}deg) translateZ(44px)`,
								backfaceVisibility: "visible",
							}}
						/>
					))}
					<div
						style={{
							position: "absolute",
							inset: "20%",
							borderRadius: "50%",
							background: `radial-gradient(circle at 35% 28%, white, ${tint} 14%, color-mix(in srgb, ${tint} 38%, #080510) 68%)`,
							boxShadow: `0 0 80px color-mix(in srgb, ${tint} 35%, transparent)`,
							transform: "translateZ(62px)",
						}}
					/>
				</div>
			</div>

			<div
				style={{
					position: "absolute",
					left: 0,
					right: 0,
					bottom: 110,
					textAlign: "center",
					color:
						"color-mix(in srgb, var(--ds-on-media-primary) 68%, transparent)",
					font: DSFont.genArtHeavy(20),
					lineHeight: 1.2,
					padding: "0 24px",
					pointerEvents: "none",
				}}
			>
				拖动或使用方向键旋转
			</div>
		</div>
	);
}
